import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from '../../../utils/toast';
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  Hourglass,
  Ban,
  Download,
  RefreshCw,
  FileText,
  CircleDollarSign,
} from 'lucide-react';
import useCobroStore from '../../../store/cobro';
import { apiErrorMessage } from '../../../utils/apiErrorMessage';
import { Button } from '../../../components/ui/Button';
import { Skeleton } from '../../../components/ui/Skeleton';
import { formatCurrency, formatDate } from '../../adminView/cobro/utils/cobroHelpers';
import {
  amountDueOf,
  paidAmountOf,
  paymentMethodLabelKey,
} from '../../adminView/cobro/utils/payments';
import { paymentMethodLabel } from './paymentMethods';

// Tras volver de la pasarela el webhook puede tardar unos segundos en confirmar,
// así que reintentamos la lectura de la nota algunas veces antes de parar.
const POLL_MAX = 4;
const POLL_MS = 4000;

const SummaryCard = ({ label, value, accent }) => (
  <div className={`bill-summary__card${accent ? ' bill-summary__card--accent' : ''}`}>
    <div className="bill-summary__label">{label}</div>
    <div className={`bill-summary__value${accent ? ' bill-summary__value--accent' : ''}`}>
      {value}
    </div>
  </div>
);

const PaymentResult = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const getMyNote = useCobroStore((s) => s.getMyNote);
  const verifyPayment = useCobroStore((s) => s.verifyPayment);

  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    // Reconciliación activa: preguntamos el estado al PSP (no dependemos del
    // webhook) y luego leemos la nota ya actualizada. Si el PSP no responde,
    // caemos al re-fetch (la nota seguirá en "confirmando" y se reintenta).
    try {
      await verifyPayment(id);
    } catch {
      /* best-effort: seguimos con el re-fetch de la nota */
    }
    const data = await getMyNote(id);
    setNote(data);
    return data;
  }, [id, getMyNote, verifyPayment]);

  useEffect(() => {
    let active = true;
    let tries = 0;
    let timer;
    const tick = async () => {
      try {
        const data = await load();
        if (!active) return;
        // El polling solo sirve para la pasarela (pending -> paid vía webhook).
        // 'in_review' depende del operador y 'paid'/'partially_paid'/'cancelled' son
        // terminales para ESTE pago: en esos casos no tiene sentido reintentar.
        const st = data?.paymentStatus;
        const settled =
          st === 'paid' || st === 'partially_paid' || st === 'in_review' || st === 'cancelled';
        if (!settled && tries < POLL_MAX) {
          tries += 1;
          timer = setTimeout(tick, POLL_MS);
        }
      } catch (err) {
        if (active) {
          toast.error(
            err?.response?.data?.message ?? t('No se pudo cargar el estado del pago.'),
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    tick();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [load, t]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, t('No se pudo actualizar el estado del pago.')));
    } finally {
      setRefreshing(false);
    }
  };

  const backToList = () => navigate('/home/pagos');
  const retry = () => navigate(`/home/pagos/${id}/pagar`);

  if (loading && !note) {
    return (
      <div className="bill bill-page" style={{ padding: 16 }} role="status" aria-live="polite">
        <Skeleton className="h-4 w-32" style={{ marginBottom: '1rem' }} />
        <div className="bill-card bill-card--pad">
          <Skeleton className="h-6 w-64" />
          <Skeleton className="h-4 w-48" style={{ marginTop: '0.5rem' }} />
        </div>
      </div>
    );
  }

  const isPaid = note?.paymentStatus === 'paid';
  const isPartial = note?.paymentStatus === 'partially_paid';
  const isInReview = note?.paymentStatus === 'in_review';
  const isCancelled =
    note?.paymentStatus === 'cancelled' || note?.status === 'cancelled';
  const cur = note?.currency ?? 'CLP';
  const amountPaid = paidAmountOf(note);
  const amountDue = amountDueOf(note);
  // El comprobante del resultado es el de ESTE pago. Se ordena por `registeredAt`
  // (fecha de registro), no por `paidAt`: el operador puede haber cargado un abono
  // con fecha posterior y no es necesariamente el último movimiento.
  const activePayments = (note?.payments ?? []).filter((payment) => !payment.voidedAt);
  const byRegistration = [...activePayments].sort(
    (a, b) =>
      new Date(a.registeredAt ?? a.paidAt ?? 0) - new Date(b.registeredAt ?? b.paidAt ?? 0),
  );
  // Contrato §1: `paymentMethod`/`paymentReference`/`paidAt` de la nota son los del
  // pago que la CERRÓ, que no tiene por qué ser el de mayor `paidAt`.
  const closingPayment = isPaid
    ? byRegistration.find(
        (payment) => note.paymentReference && payment.reference === note.paymentReference,
      ) ??
      [...byRegistration]
        .reverse()
        .find((payment) => note.paymentMethod && payment.method === note.paymentMethod) ??
      byRegistration.slice(-1)[0]
    : null;
  const lastPayment = closingPayment ?? byRegistration.slice(-1)[0];
  const paymentAmount = lastPayment?.amount ?? note?.amountPaid ?? note?.totalAmount ?? 0;

  return (
    <div className="bill bill-page" style={{ padding: 16 }}>
      <button type="button" className="bill-back" onClick={backToList}>
        <ArrowLeft className="w-[18px] h-[18px]" />
        {t('Volver a pagos')}
      </button>

      {isPaid ? (
        <>
          <div className="bill-connbanner bill-connbanner--connected">
            <span className="bill-connbanner__icon">
              <CheckCircle2 className="w-5 h-5" />
            </span>
            <div className="bill-connbanner__body">
              <span className="bill-connbanner__title">{t('Pago confirmado')}</span>
              <span className="bill-connbanner__msg">
                {t('Tu pago de la nota #{{folio}} fue recibido correctamente.', { folio: note.folio ?? '—' })}
              </span>
            </div>
          </div>

          <div className="bill-summary" style={{ marginTop: '1rem' }}>
            <SummaryCard
              label={t('Método')}
              value={t(paymentMethodLabel(note.paymentMethod ?? lastPayment?.method))}
            />
            <SummaryCard
              label={t('Pagada')}
              value={formatDate(note.paidAt ?? lastPayment?.paidAt)}
            />
            <SummaryCard
              label={t('Referencia')}
              value={note.paymentReference ?? lastPayment?.reference ?? '—'}
            />
            <SummaryCard label={t('Monto')} value={formatCurrency(paymentAmount, cur)} accent />
          </div>

          <div className="bill-card bill-card--pad" style={{ marginTop: '1rem' }}>
            <div className="flex justify-end gap-2 flex-wrap">
              {note.receiptUrl ? (
                <Button
                  variant="primary"
                  iconLeft={<Download className="w-4 h-4" />}
                  onClick={() => window.open(note.receiptUrl, '_blank', 'noopener')}
                >
                  {t('Descargar comprobante')}
                </Button>
              ) : note.pdfUrl ? (
                <Button
                  variant="primary"
                  iconLeft={<FileText className="w-4 h-4" />}
                  onClick={() => window.open(note.pdfUrl, '_blank', 'noopener')}
                >
                  {t('Descargar nota')}
                </Button>
              ) : null}
              <Button variant="outline" onClick={backToList}>
                {t('Volver a pagos')}
              </Button>
            </div>
            {!note.receiptUrl && (
              <p className="bill-hint" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
                {t('El comprobante se está generando. Si no aparece, vuelve a entrar en unos minutos o descarga la nota.')}
              </p>
            )}
          </div>
        </>
      ) : isPartial ? (
        <>
          <div className="bill-connbanner bill-connbanner--idle">
            <span className="bill-connbanner__icon">
              <CircleDollarSign className="w-5 h-5" />
            </span>
            <div className="bill-connbanner__body">
              <span className="bill-connbanner__title">{t('Pago parcial registrado')}</span>
              <span className="bill-connbanner__msg">
                {t('Tu pago de la nota #{{folio}} fue recibido. Pagaste {{paid}}; el saldo pendiente es {{due}}.', {
                  folio: note.folio ?? '—',
                  paid: formatCurrency(amountPaid, cur),
                  due: formatCurrency(amountDue, cur),
                })}
              </span>
            </div>
          </div>

          <div className="bill-summary" style={{ marginTop: '1rem' }}>
            <SummaryCard
              label={t('Método')}
              value={t(paymentMethodLabelKey(lastPayment?.method ?? note.paymentMethod))}
            />
            <SummaryCard
              label={t('Fecha de pago')}
              value={formatDate(lastPayment?.paidAt ?? note.paidAt)}
            />
            <SummaryCard
              label={t('Referencia')}
              value={lastPayment?.reference ?? note.paymentReference ?? '—'}
            />
            <SummaryCard label={t('Monto')} value={formatCurrency(paymentAmount, cur)} accent />
            <SummaryCard label={t('Saldo')} value={formatCurrency(amountDue, cur)} />
          </div>

          <div className="bill-card bill-card--pad" style={{ marginTop: '1rem' }}>
            <div className="flex justify-end gap-2 flex-wrap">
              <Button variant="primary" onClick={retry}>
                {t('Pagar el saldo')}
              </Button>
              <Button variant="outline" onClick={backToList}>
                {t('Volver a pagos')}
              </Button>
            </div>
          </div>
        </>
      ) : isInReview ? (
        <>
          <div className="bill-connbanner bill-connbanner--idle">
            <span className="bill-connbanner__icon">
              <Hourglass className="w-5 h-5" />
            </span>
            <div className="bill-connbanner__body">
              <span className="bill-connbanner__title">{t('Pago en revisión')}</span>
              <span className="bill-connbanner__msg">
                {note?.paymentMethod === 'cash'
                  ? t('Informaste tu pago en efectivo de la nota #{{folio}}.', { folio: note?.folio ?? '—' })
                  : t('Informaste tu transferencia de la nota #{{folio}}.', { folio: note?.folio ?? '—' })}{' '}
                {t('Tu operador lo revisará y confirmará el pago.')}
              </span>
            </div>
          </div>

          <div className="bill-card bill-card--pad" style={{ marginTop: '1rem' }}>
            <p className="text-fg-muted text-sm" style={{ marginTop: 0 }}>
              {t('Te avisaremos cuando el pago esté confirmado. Puedes actualizar el estado aquí.')}
            </p>
            <div className="flex justify-end gap-2 flex-wrap">
              <Button variant="outline" onClick={backToList}>
                {t('Volver a pagos')}
              </Button>
              <Button
                variant="primary"
                iconLeft={<RefreshCw className="w-4 h-4" />}
                onClick={refresh}
                loading={refreshing}
              >
                {t('Actualizar estado')}
              </Button>
            </div>
          </div>
        </>
      ) : isCancelled ? (
        <>
          <div className="bill-connbanner bill-connbanner--error">
            <span className="bill-connbanner__icon">
              <Ban className="w-5 h-5" />
            </span>
            <div className="bill-connbanner__body">
              <span className="bill-connbanner__title">{t('Nota anulada')}</span>
              <span className="bill-connbanner__msg">
                {t('Esta nota fue anulada por tu operador y no se puede pagar.')}
              </span>
            </div>
          </div>
          <div className="bill-card bill-card--pad" style={{ marginTop: '1rem' }}>
            <div className="flex justify-end">
              <Button variant="outline" onClick={backToList}>
                {t('Volver a pagos')}
              </Button>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="bill-connbanner bill-connbanner--idle">
            <span className="bill-connbanner__icon">
              <Clock className="w-5 h-5" />
            </span>
            <div className="bill-connbanner__body">
              <span className="bill-connbanner__title">{t('Estamos confirmando tu pago')}</span>
              <span className="bill-connbanner__msg">
                {t('La confirmación de la nota #{{folio}} puede tardar unos segundos. Actualiza el estado en un momento.', { folio: note?.folio ?? '—' })}
              </span>
            </div>
          </div>

          <div className="bill-card bill-card--pad" style={{ marginTop: '1rem' }}>
            <p className="text-fg-muted text-sm" style={{ marginTop: 0 }}>
              {t('Si cerraste la pasarela o el pago no se completó, puedes reiniciar el flujo y elegir nuevamente cómo pagar.')}
            </p>
            <div className="flex justify-end gap-2 flex-wrap">
              <Button variant="ghost" onClick={retry}>
                {t('Reintentar pago')}
              </Button>
              <Button variant="outline" onClick={backToList}>
                {t('Volver a pagos')}
              </Button>
              <Button
                variant="primary"
                iconLeft={<RefreshCw className="w-4 h-4" />}
                onClick={refresh}
                loading={refreshing}
              >
                {t('Actualizar estado')}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentResult;
